import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { LABEL_FCA_ML } from 'features/indicationTriage/cards/ffsCalc/levelOne/FfsLevelOneUserInputSection'
import { YieldStrengthParameterField } from 'features/indicationTriage/cards/ffsCalc/mawp/allowableStress/MetalAllowableStressTable'
import { ValueSetterPair } from 'features/indicationTriage/cards/ffsCalc/typeUtils'
import React, { FC, useContext, useMemo, useState } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

type MeasType = GeckoMeasurement | null
export type LevelTwoUserInputSectionProps = ValueSetterPair<
  MeasType,
  'designPress'
> &
  ValueSetterPair<MeasType, 'designTemp'> &
  ValueSetterPair<MeasType, 'thicknessAvgLen'>

export function useLevelTwoUserInputSectionProps(): LevelTwoUserInputSectionProps {
  const [designPress, setDesignPress] = useState<MeasType>(
    GeckoMeasurement.CreateFromValue(
      150,
      GeckoMeasurementUnit.PoundsForcePerSqInch
    )
  )
  const [designTemp, setDesignTemp] = useState<MeasType>(
    GeckoMeasurement.CreateFromValue(30, GeckoMeasurementUnit.Celsius)
  )
  const [thicknessAvgLen, setThicknessAvgLen] = useState<MeasType>(
    GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches)
  )

  return useMemo(
    () => ({
      designPress,
      setDesignPress,
      designTemp,
      setDesignTemp,
      thicknessAvgLen,
      setThicknessAvgLen,
    }),
    [designPress, designTemp, thicknessAvgLen]
  )
}

const RawLevelTwoUserInputSection: FC<LevelTwoUserInputSectionProps> = ({
  designTemp,
  setDesignTemp,
  designPress,
  setDesignPress,
  thicknessAvgLen,
  setThicknessAvgLen,
}) => {
  const {
    fca,
    setFca,
    jointEfficiency,
    setJointEfficiency,
    yieldStrength,
    setYieldStrength,
    tensileStrength,
    setTensileStrength,
    fcaMl,
    setFcaMl,
  } = useContext(FfsSharedValuesContext)
  return (
    <SectionContainer>
      <FfsParameterField
        label='Design Temp'
        labelTooltip='Component design temperature'
        labelWidth={LABEL_WIDTH}
        value={designTemp}
        setValue={setDesignTemp}
        measurementType='temperature'
        min={GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Celsius)}
        step={5}
      />
      <FfsParameterField
        label='Design Press'
        labelTooltip='Component design pressure'
        labelWidth={LABEL_WIDTH}
        value={designPress}
        setValue={setDesignPress}
        measurementType='pressure'
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Megapascals
        )}
        step={1}
      />
      <YieldStrengthParameterField
        stressValue={yieldStrength}
        setStressValue={setYieldStrength}
        tensileStrength={tensileStrength}
        setTensileStrength={setTensileStrength}
        labelWidth={LABEL_WIDTH}
      />
      <FfsParameterField
        label='FCA'
        labelTooltip='Future corrosion allowance'
        labelWidth={LABEL_WIDTH}
        value={fca}
        setValue={setFca}
        measurementType='thickness'
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Millimeters
        )}
        step={0.1}
      />
      <FfsParameterField
        label={LABEL_FCA_ML}
        labelTooltip='FCA in region of metal loss'
        labelWidth={LABEL_WIDTH}
        value={fcaMl}
        setValue={setFcaMl}
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Millimeters
        )}
        step={0.1}
      />
      <FfsParameterField
        label='E'
        labelTooltip='Joint Efficiency'
        labelWidth={LABEL_WIDTH}
        value={jointEfficiency}
        setValue={setJointEfficiency}
        measurementType='ratio'
        min={GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Ratio)}
        max={GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Ratio)}
        step={0.05}
      />
      <FfsParameterField
        label='L'
        labelTooltip='Thickness averaging length'
        labelWidth={LABEL_WIDTH}
        value={thicknessAvgLen}
        setValue={setThicknessAvgLen}
        measurementType='thickness'
        min={GeckoMeasurement.CreateFromValue(
          0.5,
          GeckoMeasurementUnit.Millimeters
        )}
        max={GeckoMeasurement.CreateFromValue(
          5000,
          GeckoMeasurementUnit.Millimeters
        )}
        step={1}
      />
    </SectionContainer>
  )
}

export const LevelTwoUserInputSection = React.memo(RawLevelTwoUserInputSection)
